import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';

import { BaseHttpService } from '@framework/services';
import { StepOutputApiModel, TagOutputApiModel, StoryOutputApiModel, StorySettingOutputApiModel, StoryFacetedSearchOutputApiModel } from '@modules/story/models/api-models/output';
import { CtaTemplateOutputApiModel } from '@app/store/http/models/output';
import { GetStoriesInputApiModel, SearchStoryInputApiModel } from '../models/api-models/input';
import { ResponsePaginationApiModel } from '@framework/models/api-models';
import { UpdateStepTagSettingInputApiModel } from '@modules/company-admin/store/models/api-models/input';

@Injectable({ providedIn: 'root' })
export class CompanyHttpService extends BaseHttpService {

    constructor(protected http: HttpClient) {
        super(http, '/companies');
    }

    public getContributionStep(companyId: number) {
        return this.get('/' + companyId + '/setting/story-steps/contributions/').pipe(map((response: StepOutputApiModel[]) => {
            return response;
        }));
    }

    public getStepsAuthorized(companyId: number, authorization: string, isInbound: boolean = false) {
        const path = isInbound
            ? `/${companyId}/setting/story-steps/inbound/`
            : `/${companyId}/setting/story-steps/`;
        return this.getWithHeaders<Array<StepOutputApiModel>>(path, {
            Authorization: authorization,
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache'
        }, {
            timestamp: Date.now().toString()
        }).pipe(
            map((response: Array<StepOutputApiModel>) => {
                return (response || []).map(r => Object.assign({}, r, {
                    tags: r.tags && r.tags.map(tag => new TagOutputApiModel(tag))
                }));
            })
        );
    }

    public getSteps(companyId: number, isInbound: boolean = false, tagSelected: string, activeOnly: boolean = false) {
        let apiUrl = '/' + companyId + '/setting/story-steps/';
        if (isInbound) {
            apiUrl = '/' + companyId + '/setting/story-steps/inbound/';
        }
        if (tagSelected) {
            apiUrl += `?selected=${tagSelected}`;
        }
        if (activeOnly) {
            apiUrl += !tagSelected ? '?' : '&';
            apiUrl += `ao=true`;
        }
        return this.getCampaign(apiUrl).pipe(
            map((response: Array<StepOutputApiModel>) => {
                return response.map(r => Object.assign({}, r, {
                    tags: r.tags && r.tags.map(tag => new TagOutputApiModel(tag))
                }));
            }));
    }

    public getCtaTemplates(companyId: number, isInbound: boolean) {
        let url = '/' + companyId + '/setting/cta-templates/';
        if (isInbound === true) {
            url = '/' + companyId + '/setting/cta-templates/inbound/';
        }
        return this.getCampaign(url).pipe(
            map((response: Array<CtaTemplateOutputApiModel>) => {
                return response;
            }));
    }

    public changeUserTitle(companyId,uid,payload){
        return this.patch(`/${companyId}/setting/users/${uid}/title/`,payload).pipe(
            map((response: any) => {
                return response;
            }));
    }

    public getTestimoniaSetting(companyId: number) {
        return this.get(`/${companyId}/setting/testimonial/`).pipe(
            map((response: StorySettingOutputApiModel) => {
                return response;
            }));
    }

    public getStories(queryData: SearchStoryInputApiModel) {
        return this.get(`/${queryData.companyId}/stories/?${queryData.query}`).pipe(
            map((response: ResponsePaginationApiModel<StoryOutputApiModel, StoryFacetedSearchOutputApiModel>) => {
                return response;
            })
        );
    }

    public getFrequentTags(queryData: SearchStoryInputApiModel) {
        return this.get(`/${queryData.companyId}/stories/frequent_tags/?${queryData.query}`).pipe(
            map((response: ResponsePaginationApiModel<StoryOutputApiModel, StoryFacetedSearchOutputApiModel>) => {
                return response;
            })
        );
    }

    public getCompanySetting(slug: string) {
        return this.get(`/${slug}/setting/`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public updateStepTagSetting(companyId: number, id: number, isInbound: boolean, isStep: boolean, payload: Partial<UpdateStepTagSettingInputApiModel>) {
        const apiUrl = `/${companyId}/setting/${isStep ? 'testimonial-story-steps' : 'testimonial-story-tags'}/${id}/${isInbound ? '?inbound=true' : ''}`;
        return this.patch(apiUrl, payload).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public createStepTagSetting(companyId: number, stepId:number, isInbound: boolean, isStep: boolean, payload: Partial<UpdateStepTagSettingInputApiModel>) {
        const apiUrl = `/${companyId}/setting/${isStep ? 'testimonial-story-steps' : 'testimonial-story-tags'}/${stepId}/add/${isInbound ? '?inbound=true' : ''}`;
        return this.post(apiUrl, payload).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public createStepTagSettings(companyId: number, stepId: number, isInbound: boolean, texts: string[]) {
        const apiUrl = `/${companyId}/setting/testimonial-story-tags/${stepId}/add-many/${isInbound ? '?inbound=true' : ''}`;
        return this.post(apiUrl, { texts }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public bulkUpdateAdminTags(companyId: number, questionId: number, payload: { action: string, ids: number[] }, authorization: string, isInbound: boolean = false) {
        const query = isInbound ? '?inbound=true' : '';
        return this.post(`/${companyId}/setting/testimonial-story-tags/${questionId}/bulk-action/${query}`, payload, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public moveStoryTags(companyId: number, direction: 'user-to-guest' | 'guest-to-user', ids: number[], authorization: string) {
        return this.post(`/${companyId}/setting/testimonial-story-tags/move/`, { direction, ids }, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public copyStoryTagSettings(companyId: number, direction: 'user-to-guest' | 'guest-to-user', authorization: string) {
        return this.post(`/${companyId}/setting/testimonial-story-tags/copy/`, { direction }, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public bulkUploadAdminTags(companyId: number, questionId: number, file: File, authorization: string, isInbound: boolean = false, mirror: boolean = false) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (mirror) {
            formData.append('mirror', 'true');
        }
        const query = isInbound ? '?inbound=true' : '';
        return this.post(`/${companyId}/setting/testimonial-story-tags/${questionId}/bulk/${query}`, formData, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public deleteStepTagSetting(companyId: number, id: number, isInbound: boolean, isStep: boolean) {
        const apiUrl = `/${companyId}/setting/${isStep ? 'testimonial-story-steps' : 'testimonial-story-tags'}/${id}/${isInbound ? '?inbound=true' : ''}`;
        return this.delete(apiUrl).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public getQuestionsList(companyId){
        return this.getCampaign(`/${companyId}/setting/story-question/`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public addQuestionsList(companyId,data){
        return this.post(`/${companyId}/setting/story-question/`,data).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public previewStoryQuestionsCsv(companyId: number, file: File, authorization: string) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('preview', 'true');
        return this.post(`/${companyId}/setting/story-question/bulk/`, formData, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public bulkUploadStoryQuestions(companyId: number, file: File, mapping: { [key: string]: string }, authorization: string) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        Object.keys(mapping).forEach((key) => formData.append(key, mapping[key]));
        return this.post(`/${companyId}/setting/story-question/bulk/`, formData, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public bulkActionStoryQuestions(companyId: number, payload: { action: 'delete' | 'set_audience'; ids: number[]; audience?: string }, authorization: string) {
        return this.post(`/${companyId}/setting/story-question/bulk-action/`, payload, {
            headers: { Authorization: authorization }
        }).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public editQuestionsList(companyId,questionId,payload){
        return this.patch(`/${companyId}/setting/story-question/${questionId}/`,payload).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public deleteQuestionsList(companyId,questionId){
        return this.delete(`/${companyId}/setting/story-question/${questionId}/`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public getNotes(companyId,storyId){
        return this.get(`/${companyId}/setting/story-notes/?id_story=${storyId}`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public addNoteList(companyId,data){
        return this.post(`/${companyId}/setting/story-notes/`,data).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public editNotesList(companyId,noteId,payload){
        return this.patch(`/${companyId}/setting/story-notes/${noteId}/`,payload).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    public deleteNoteList(companyId,noteId){
        return this.delete(`/${companyId}/setting/story-notes/${noteId}/`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    public getReview(companyId,storyId,queryParams){
        return this.get(`/${companyId}/${storyId}/review_sites/?${queryParams}`).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
}
